import { useState, useEffect, useCallback } from 'react';
import { FileText, Search, Filter, Calendar, Eye, Edit2, XCircle, Plus, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { revenueAPI, storesAPI, manufacturersAPI, vendorsAPI } from '@/services/api';

const ReceivableInvoicesPage = () => {
  const { toast } = useToast();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [filters, setFilters] = useState({
    party_type: 'all',
    type: 'all',
    status: 'all',
    store_id: 'all'
  });
  
  // Metadata
  const [stores, setStores] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [vendors, setVendors] = useState([]);
  
  // Dialog states
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [billingPeriod, setBillingPeriod] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [generating, setGenerating] = useState(false);

  const fetchInvoices = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.party_type !== 'all') params.party_type = filters.party_type;
      if (filters.type !== 'all') params.type = filters.type;
      if (filters.status !== 'all') params.status = filters.status;
      if (filters.store_id !== 'all') params.store_id = filters.store_id;
      
      const response = await revenueAPI.listInvoices(params);
      setInvoices(response.data || []);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch invoices', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [filters, toast]);

  const fetchMetadata = async () => {
    try {
      const [storesRes, mfrsRes, vendorsRes] = await Promise.all([
        storesAPI.list(),
        manufacturersAPI.dropdown(),
        vendorsAPI.list()
      ]);
      setStores(storesRes.data || []);
      setManufacturers(mfrsRes.data || []);
      setVendors(vendorsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch metadata:', error);
    }
  };

  useEffect(() => {
    fetchInvoices();
    fetchMetadata();
  }, [fetchInvoices]);

  const handleView = async (invoice) => {
    try {
      const response = await revenueAPI.getInvoice(invoice.id);
      setSelectedInvoice(response.data);
      setViewDialogOpen(true);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch invoice details', variant: 'destructive' });
    }
  };

  const handleCancel = async () => {
    if (!cancelReason.trim()) {
      toast({ title: 'Error', description: 'Please provide a cancellation reason', variant: 'destructive' });
      return;
    }
    
    try {
      await revenueAPI.cancelInvoice(selectedInvoice.id, { cancel_reason: cancelReason });
      toast({ title: 'Success', description: 'Invoice cancelled successfully' });
      setCancelDialogOpen(false);
      setCancelReason('');
      fetchInvoices();
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to cancel invoice', 
        variant: 'destructive' 
      });
    }
  };

  const handleGenerateMonthly = async () => {
    setGenerating(true);
    try {
      const response = await revenueAPI.generateMonthlyInvoices(billingPeriod);
      toast({ 
        title: 'Success', 
        description: `Generated ${response.data.invoices_created} invoice(s) for ${billingPeriod}` 
      });
      setGenerateDialogOpen(false);
      fetchInvoices();
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to generate invoices', 
        variant: 'destructive' 
      });
    } finally {
      setGenerating(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-700',
      partial: 'bg-blue-100 text-blue-700',
      paid: 'bg-green-100 text-green-700',
      cancelled: 'bg-red-100 text-red-700'
    };
    return <Badge className={styles[status] || 'bg-gray-100 text-gray-700'}>{status.toUpperCase()}</Badge>;
  };

  const getTypeBadge = (type) => {
    const styles = {
      vendor_registration: 'bg-blue-50 text-blue-600 border-blue-200',
      brand_registration: 'bg-purple-50 text-purple-600 border-purple-200',
      product_registration: 'bg-teal-50 text-teal-600 border-teal-200',
      cell_rental_booking: 'bg-amber-50 text-amber-600 border-amber-200',
      display_ad_booking: 'bg-pink-50 text-pink-600 border-pink-200',
      brand_staff_hiring: 'bg-indigo-50 text-indigo-600 border-indigo-200',
      barcode_change: 'bg-orange-50 text-orange-600 border-orange-200'
    };
    const labels = {
      vendor_registration: 'Vendor Registration',
      brand_registration: 'Brand Registration',
      product_registration: 'Product Registration',
      cell_rental_booking: 'Shelf Rental',
      display_ad_booking: 'Display Ad',
      brand_staff_hiring: 'Brand Staff Hiring',
      barcode_change: 'Barcode Change'
    };
    return <Badge variant="outline" className={styles[type] || ''}>{labels[type] || type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</Badge>;
  };

  return (
    <div className="p-6 space-y-6" data-testid="receivable-invoices-page">
      <Toaster />
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-teal-100 rounded-lg">
            <FileText className="w-6 h-6 text-teal-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Receivable Invoices</h1>
            <p className="text-gray-500 text-sm">Manage invoices from vendors and manufacturers</p>
          </div>
        </div>
        <Button onClick={() => setGenerateDialogOpen(true)} className="bg-teal-600 hover:bg-teal-700" data-testid="generate-monthly-btn">
          <RefreshCw className="w-4 h-4 mr-2" /> Generate Monthly Invoices
        </Button>
      </div>

      {/* Filters */}
      <Card className="bg-white border-gray-200">
        <CardContent className="p-4">
          <div className="grid grid-cols-4 gap-4">
            <Select value={filters.party_type} onValueChange={(v) => setFilters(p => ({ ...p, party_type: v }))}>
              <SelectTrigger className="bg-gray-50 border-gray-300">
                <SelectValue placeholder="Party Type" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">All Parties</SelectItem>
                <SelectItem value="vendor">Vendors</SelectItem>
                <SelectItem value="manufacturer">Manufacturers</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={filters.type} onValueChange={(v) => setFilters(p => ({ ...p, type: v }))}>
              <SelectTrigger className="bg-gray-50 border-gray-300">
                <SelectValue placeholder="Invoice Type" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="vendor_registration">Vendor Registration</SelectItem>
                <SelectItem value="brand_registration">Brand Registration</SelectItem>
                <SelectItem value="product_registration">Product Registration</SelectItem>
                <SelectItem value="cell_rental_booking">Shelf Rental</SelectItem>
                <SelectItem value="display_ad_booking">Display Ad</SelectItem>
                <SelectItem value="brand_staff_hiring">Brand Staff Hiring</SelectItem>
                <SelectItem value="barcode_change">Barcode Change</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={filters.status} onValueChange={(v) => setFilters(p => ({ ...p, status: v }))}>
              <SelectTrigger className="bg-gray-50 border-gray-300">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="partial">Partial</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={filters.store_id} onValueChange={(v) => setFilters(p => ({ ...p, store_id: v }))}>
              <SelectTrigger className="bg-gray-50 border-gray-300">
                <SelectValue placeholder="Store" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">All Stores</SelectItem>
                {stores.map(s => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="bg-white border-gray-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
            </div>
          ) : invoices.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <FileText className="w-12 h-12 mb-4 text-gray-300" />
              <p className="text-lg font-medium">No Invoices Found</p>
              <p className="text-sm">Invoices will appear here when generated</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="text-gray-600">Invoice #</TableHead>
                  <TableHead className="text-gray-600">Date</TableHead>
                  <TableHead className="text-gray-600">Party</TableHead>
                  <TableHead className="text-gray-600">Type</TableHead>
                  <TableHead className="text-gray-600">Store</TableHead>
                  <TableHead className="text-gray-600 text-right">Amount</TableHead>
                  <TableHead className="text-gray-600 text-right">Balance</TableHead>
                  <TableHead className="text-gray-600">Status</TableHead>
                  <TableHead className="text-gray-600 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.map((inv) => (
                  <TableRow key={inv.id} className="border-gray-200 hover:bg-gray-50">
                    <TableCell>
                      <span className="font-mono font-medium text-gray-800">{inv.invoice_number}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-gray-600">{new Date(inv.invoice_date).toLocaleDateString()}</span>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium text-gray-800">{inv.party_name}</p>
                        <p className="text-xs text-gray-500 capitalize">{inv.party_type}</p>
                      </div>
                    </TableCell>
                    <TableCell>{getTypeBadge(inv.type)}</TableCell>
                    <TableCell>
                      <span className="text-gray-600">{inv.store_name || 'All Stores'}</span>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className="font-medium">₹{inv.grand_total?.toLocaleString()}</span>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className={inv.balance_amount > 0 ? 'text-red-600 font-medium' : 'text-green-600'}>
                        ₹{inv.balance_amount?.toLocaleString()}
                      </span>
                    </TableCell>
                    <TableCell>{getStatusBadge(inv.status)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleView(inv)}>
                          <Eye className="w-4 h-4 text-gray-500" />
                        </Button>
                        {inv.status !== 'cancelled' && inv.status !== 'paid' && (
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setSelectedInvoice(inv); setCancelDialogOpen(true); }}>
                            <XCircle className="w-4 h-4 text-red-500" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-2xl bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-gray-800">
              <FileText className="w-5 h-5 text-teal-600" />
              Invoice {selectedInvoice?.invoice_number}
            </DialogTitle>
          </DialogHeader>
          
          {selectedInvoice && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Party</p>
                  <p className="font-medium">{selectedInvoice.party_name}</p>
                  <p className="text-xs text-gray-400 capitalize">{selectedInvoice.party_type}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Invoice Date</p>
                  <p className="font-medium">{new Date(selectedInvoice.invoice_date).toLocaleDateString()}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Type</p>
                  {getTypeBadge(selectedInvoice.type)}
                </div>
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  {getStatusBadge(selectedInvoice.status)}
                </div>
                <div>
                  <p className="text-sm text-gray-500">Store</p>
                  <p className="font-medium">{selectedInvoice.store_name || 'All Stores'}</p>
                </div>
                {selectedInvoice.billing_period && (
                  <div>
                    <p className="text-sm text-gray-500">Billing Period</p>
                    <p className="font-medium">{selectedInvoice.billing_period}</p>
                  </div>
                )}
              </div>
              
              {/* Line Items */}
              <div>
                <p className="text-sm text-gray-500 mb-2">Line Items</p>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead>Description</TableHead>
                      <TableHead className="text-right">Qty</TableHead>
                      <TableHead className="text-right">Rate</TableHead>
                      <TableHead className="text-right">Tax</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedInvoice.line_items?.map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{item.description}</TableCell>
                        <TableCell className="text-right">{item.quantity}</TableCell>
                        <TableCell className="text-right">₹{item.rate}</TableCell>
                        <TableCell className="text-right">{item.tax_percent}%</TableCell>
                        <TableCell className="text-right font-medium">₹{item.total}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              
              {/* Totals */}
              <div className="flex justify-end">
                <div className="w-64 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Subtotal</span>
                    <span>₹{selectedInvoice.subtotal?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Tax</span>
                    <span>₹{selectedInvoice.total_tax?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between border-t pt-2">
                    <span className="font-medium">Grand Total</span>
                    <span className="font-bold">₹{selectedInvoice.grand_total?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Paid</span>
                    <span className="text-green-600">₹{selectedInvoice.paid_amount?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Balance</span>
                    <span className={selectedInvoice.balance_amount > 0 ? 'text-red-600 font-medium' : 'text-green-600'}>
                      ₹{selectedInvoice.balance_amount?.toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>

              {selectedInvoice.cancel_reason && (
                <div className="p-3 bg-red-50 rounded-lg">
                  <p className="text-sm text-red-600"><strong>Cancelled:</strong> {selectedInvoice.cancel_reason}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Cancel Dialog */}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="text-red-600">Cancel Invoice</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-600 mb-4">
              Are you sure you want to cancel invoice <strong>{selectedInvoice?.invoice_number}</strong>?
            </p>
            <div className="space-y-2">
              <Label className="text-gray-700">Reason for cancellation *</Label>
              <Textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Enter reason..."
                className="bg-gray-50 border-gray-300"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelDialogOpen(false)}>Back</Button>
            <Button onClick={handleCancel} className="bg-red-600 hover:bg-red-700">Cancel Invoice</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Generate Monthly Dialog */}
      <Dialog open={generateDialogOpen} onOpenChange={setGenerateDialogOpen}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-teal-600" />
              Generate Monthly Invoices
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-gray-600">
              This will generate rental invoices for all active manufacturer subscriptions for the selected billing period.
            </p>
            <div className="space-y-2">
              <Label className="text-gray-700">Billing Period (YYYY-MM)</Label>
              <Input
                value={billingPeriod}
                onChange={(e) => setBillingPeriod(e.target.value)}
                placeholder="2026-02"
                className="bg-gray-50 border-gray-300"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGenerateDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleGenerateMonthly} disabled={generating} className="bg-teal-600 hover:bg-teal-700">
              {generating ? 'Generating...' : 'Generate Invoices'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReceivableInvoicesPage;
